import { ProductPageTemplate } from "@/components/ProductPageTemplate";
import { DollarSign, Clock, Shield, TrendingUp, Building, AlertTriangle } from "lucide-react";
import {getImageUrl} from '@/lib/utils';

const BusinessInterruption = () => {
  const pageData = {
    // SEO Meta Data
    title: "Business Interruption Insurance UAE | Loss of Income Coverage Dubai | YallaCompare",
    metaDescription: "Get business interruption insurance in UAE. Protect your income from business disruptions in Dubai, Abu Dhabi & UAE. Compare loss of income insurance quotes.",

    // Hero Section
    heroTitle: "Business Interruption Insurance for UAE Businesses",
    heroSubtitle: "Income Protection Coverage",
    heroDescription: "Protect your business income from unexpected interruptions. Coverage for lost revenue, ongoing expenses, and business continuity in the UAE.",
    heroImage: getImageUrl("business-interruption-icon.png"),

    // Key Benefits
    keyBenefits: [
      "Lost income and revenue protection",
      "Coverage for ongoing business expenses",
      "Extended period coverage options",
      "Quick claim processing and settlements",
      "Temporary relocation expense coverage",
      "Professional business continuity support"
    ],

    // Features Section
    features: [
      {
        title: "Lost Income Protection",
        description: "Compensation for lost revenue and profits during business interruption periods.",
        icon: <DollarSign className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Ongoing Expense Coverage",
        description: "Coverage for continuing expenses like rent, salaries, and loan payments during closure.",
        icon: <Building className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Extended Period Coverage",
        description: "Protection during the time needed to restore business to pre-loss levels.",
        icon: <Clock className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Additional Expense Coverage",
        description: "Reimbursement for extra costs to minimize business interruption impact.",
        icon: <TrendingUp className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Supplier Dependency",
        description: "Coverage for losses due to interruptions at key suppliers or customers.",
        icon: <Shield className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Professional Support",
        description: "Access to business continuity experts and recovery specialists.",
        icon: <AlertTriangle className="h-6 w-6 text-blue-600" />
      }
    ],

    // Coverage Details
    coverageDetails: [
      {
        title: "Income Protection",
        description: "Comprehensive coverage for lost business income and profits",
        included: [
          "Lost business income and profits",
          "Continuing operating expenses",
          "Employee salaries and benefits",
          "Rent and lease payments",
          "Loan and mortgage payments"
        ]
      },
      {
        title: "Additional Expenses",
        description: "Coverage for extra costs during business interruption",
        included: [
          "Utilities and insurance premiums",
          "Additional operating expenses",
          "Temporary relocation costs",
          "Marketing and advertising costs",
          "Professional fees for recovery"
        ]
      }
    ],

    // Why Choose Us
    whyChooseUs: [
      {
        title: "Financial Stability",
        description: "Maintain cash flow and financial stability during unexpected business interruptions"
      },
      {
        title: "Employee Protection",
        description: "Continue paying salaries and benefits to retain valuable employees during closure"
      },
      {
        title: "Faster Recovery",
        description: "Access to funds and resources needed for quicker business recovery and restoration"
      },
      {
        title: "Competitive Advantage",
        description: "Maintain market position and customer relationships during recovery period"
      }
    ],

    // FAQ Section
    faqs: [
      {
        question: "What events trigger business interruption coverage?",
        answer: "Coverage typically applies to interruptions caused by covered property damage events like fire, flood, theft, or other insured perils that force business closure."
      },
      {
        question: "How is the coverage amount calculated?",
        answer: "Coverage is based on your historical financial records, projected income, and ongoing expenses. Most policies cover 12-24 months of lost income."
      },
      {
        question: "Is there a waiting period before coverage begins?",
        answer: "Yes, most policies have a waiting period (typically 24-72 hours) before business interruption coverage begins after the triggering event."
      },
      {
        question: "Does it cover interruptions due to pandemics?",
        answer: "Standard policies typically exclude pandemics. Specific pandemic coverage may be available as an add-on or separate policy."
      },
      {
        question: "Can I get coverage for supplier interruptions?",
        answer: "Yes, contingent business interruption coverage can protect against losses from interruptions at key suppliers, customers, or business partners."
      }
    ],

    // CTA Section
    ctaTitle: "Secure Your Business Income Today",
    ctaDescription: "Protect your business from unexpected interruptions with comprehensive business interruption insurance. Get quotes from leading UAE insurers and ensure business continuity.",
    primaryCta: "Get Business Interruption Quote",
    secondaryCta: "Compare All Insurance Products",

    // Product ID for quote form
    productId: "business-interruption"
  };

  return <ProductPageTemplate {...pageData} />;
};

export default BusinessInterruption;
